import React, { useEffect, useState } from 'react';
import {
  MessageSquare,
  Search,
  Filter,
  Phone,
  Mail,
  Linkedin,
  Calendar,
  Trash2,
  CheckCircle,
  ExternalLink,
  RefreshCw,
  Eye,
  X,
} from 'lucide-react';
import { portalApi } from '../../../../lib/portalApi';

const STATUS_BADGES = {
  new: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
  contacted: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  in_discussion: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  archived: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

const ROLE_COLORS = {
  Founder: 'bg-[#d42b92]/20 text-[#ff8ec5] border-[#d42b92]/40',
  Investor: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
  'Business / Corporate': 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  'Enabler / Ecosystem Partner': 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  'Mentor / Expert': 'bg-teal-500/20 text-teal-300 border-teal-500/40',
  Explorer: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40',
};

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = () => {
    setLoading(true);
    portalApi
      .listContacts()
      .then((data) => {
        setContacts(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Failed to load contacts:', err);
        setContacts([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await portalApi.patchContact(id, { status });
      setContacts((prev) =>
        prev.map((c) => (c._id === id ? { ...c, status } : c))
      );
      if (selectedContact && selectedContact._id === id) {
        setSelectedContact((prev) => ({ ...prev, status }));
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact submission?')) return;
    try {
      await portalApi.deleteContact(id);
      setContacts((prev) => prev.filter((c) => c._id !== id));
      if (selectedContact && selectedContact._id === id) {
        setSelectedContact(null);
      }
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const filteredContacts = contacts.filter((c) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.message?.toLowerCase().includes(q);
    const matchesRole = !roleFilter || c.role === roleFilter;
    const matchesStatus = !statusFilter || (c.status || 'new') === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2.5">
            <MessageSquare className="w-6 h-6 text-[#ff8ec5]" />
            <span>Contact Inquiries</span>
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Contact Us + newsletter subscriptions from the website ({contacts.length} total)
          </p>
        </div>

        <button
          type="button"
          onClick={fetchContacts}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white/80 hover:text-white hover:border-[#d42b92]/50 text-sm transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, or message..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-black/40 border border-white/10 text-white text-sm placeholder:text-white/35 focus:outline-none focus:border-[#d42b92]/60"
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
        >
          <option value="">All Roles</option>
          <option value="Founder">Founder</option>
          <option value="Investor">Investor</option>
          <option value="Business / Corporate">Business / Corporate</option>
          <option value="Enabler / Ecosystem Partner">Enabler / Ecosystem Partner</option>
          <option value="Mentor / Expert">Mentor / Expert</option>
          <option value="Explorer">Explorer</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]/60"
        >
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="in_discussion">In Discussion</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Data Table */}
      {loading ? (
        <div className="p-12 text-center text-white/40 border border-white/10 rounded-2xl bg-white/[0.01]">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-[#d42b92]" />
          <p>Loading contact submissions…</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="p-12 text-center text-white/40 border border-white/10 rounded-2xl bg-white/[0.01]">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 text-white/20" />
          <p className="text-white/70 font-medium">No contact submissions found</p>
          <p className="text-xs text-white/40 mt-1">Submissions from the Contact Us page will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.01]">
          <table className="w-full text-left text-sm">
            <thead className="bg-white/[0.04] text-white/50 border-b border-white/10">
              <tr>
                <th className="px-4 py-3.5 font-medium">Contact</th>
                <th className="px-4 py-3.5 font-medium">Role</th>
                <th className="px-4 py-3.5 font-medium">Interests</th>
                <th className="px-4 py-3.5 font-medium">Submitted</th>
                <th className="px-4 py-3.5 font-medium">Status</th>
                <th className="px-4 py-3.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredContacts.map((c) => {
                const statusKey = c.status || 'new';
                const statusClass = STATUS_BADGES[statusKey] || STATUS_BADGES.new;
                const roleClass = ROLE_COLORS[c.role] || 'bg-white/10 text-white/80 border-white/20';

                return (
                  <tr
                    key={c._id}
                    className="hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={() => setSelectedContact(c)}
                  >
                    <td className="px-4 py-3.5">
                      <div className="font-semibold text-white">{c.name}</div>
                      <div className="text-xs text-white/50 flex items-center gap-2 mt-0.5">
                        <span>{c.email}</span>
                        {c.phone && <span>· {c.phone}</span>}
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${roleClass}`}>
                        {c.role || 'Unspecified'}
                      </span>
                    </td>

                    <td className="px-4 py-3.5 max-w-[200px]">
                      <div className="text-xs text-white/80 truncate">
                        {Array.isArray(c.interests) && c.interests.length > 0
                          ? c.interests.join(', ')
                          : c.enquiryType || 'General'}
                      </div>
                    </td>

                    <td className="px-4 py-3.5 text-xs text-white/50 whitespace-nowrap">
                      {c.createdAt
                        ? new Date(c.createdAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </td>

                    <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={statusKey}
                        onChange={(e) => handleStatusChange(c._id, e.target.value)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full border bg-black/60 focus:outline-none cursor-pointer ${statusClass}`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="in_discussion">In Discussion</option>
                        <option value="archived">Archived</option>
                      </select>
                    </td>

                    <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedContact(c)}
                          className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(c._id)}
                          className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-xl rounded-2xl border border-white/15 bg-[#101015] p-6 sm:p-8 shadow-2xl space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[11px] font-semibold text-[#ff8ec5] tracking-wider uppercase">
                  Contact Inquiry Detail
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white mt-1">
                  {selectedContact.name}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedContact(null)}
                className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm pt-2 border-t border-white/10">
              <div>
                <div className="text-xs text-white/40">Email</div>
                <a
                  href={`mailto:${selectedContact.email}`}
                  className="text-white hover:text-[#ff8ec5] font-medium flex items-center gap-1.5 mt-0.5"
                >
                  <Mail className="w-3.5 h-3.5 text-[#ff8ec5]" />
                  <span>{selectedContact.email}</span>
                </a>
              </div>

              <div>
                <div className="text-xs text-white/40">Phone / WhatsApp</div>
                <a
                  href={`tel:${selectedContact.phone}`}
                  className="text-white hover:text-[#ff8ec5] font-medium flex items-center gap-1.5 mt-0.5"
                >
                  <Phone className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{selectedContact.phone || '—'}</span>
                </a>
              </div>

              <div>
                <div className="text-xs text-white/40">Identifies As</div>
                <span className="mt-1 inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40">
                  {selectedContact.role || 'Unspecified'}
                </span>
              </div>

              <div>
                <div className="text-xs text-white/40">Submitted On</div>
                <div className="text-white/80 mt-1 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-white/40" />
                  <span>
                    {selectedContact.createdAt
                      ? new Date(selectedContact.createdAt).toLocaleString()
                      : '—'}
                  </span>
                </div>
              </div>

              {selectedContact.linkedin && (
                <div className="col-span-2">
                  <div className="text-xs text-white/40">LinkedIn Profile</div>
                  <a
                    href={selectedContact.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#ff8ec5] hover:underline flex items-center gap-1.5 mt-0.5"
                  >
                    <Linkedin className="w-3.5 h-3.5 text-[#ff8ec5]" />
                    <span>{selectedContact.linkedin}</span>
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                </div>
              )}

              <div className="col-span-2">
                <div className="text-xs text-white/40">Connecting For</div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {(Array.isArray(selectedContact.interests) && selectedContact.interests.length > 0
                    ? selectedContact.interests
                    : [selectedContact.enquiryType || 'General Inquiry']
                  ).map((item) => (
                    <span
                      key={item}
                      className="px-2.5 py-1 rounded-lg text-xs bg-white/[0.05] border border-white/10 text-white/90"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="col-span-2">
                <div className="text-xs text-white/40">Message / Formatted Note</div>
                <div className="mt-1.5 p-3 rounded-xl bg-black/50 border border-white/10 text-white/80 text-xs sm:text-sm whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto">
                  {selectedContact.message || 'No additional message provided.'}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/40">Status:</span>
                <select
                  value={selectedContact.status || 'new'}
                  onChange={(e) => handleStatusChange(selectedContact._id, e.target.value)}
                  className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-black/60 text-white focus:outline-none cursor-pointer"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="in_discussion">In Discussion</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleDelete(selectedContact._id)}
                  className="px-3.5 py-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs transition-colors flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedContact(null)}
                  className="px-4 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
